/**
 * Settings → Nightshift → Models: the Nightshift-wide route lists.
 *
 *   - Default agent routes: the bottom of the execution cascade. Refining, Implementing and
 *     AI Review fall through to these when neither the ticket nor the project's
 *     `[execution.*]` tables name a route.
 *   - Light routes, one list per stage: what a small ticket tries first where the default agent
 *     routes apply, with the route evidence table beneath to choose them from.
 *   - Utility routes: what cheap background jobs (footprints, comparisons, card titles) run on
 *     unless a project sets its own list under Project → Sidecars.
 *
 * Each is the same four-column list as everywhere else. While a default list is empty the coded
 * fallback applies and the page says which one, so "nothing saved" is never a mystery.
 */
import { useCallback, useEffect, useState } from 'react';

import type { UiCatalogueModel, UiModelDefaults } from '@nightshift/ui-types';

import { Empty, Kicker, Spinner } from '../components/primitives.tsx';
import { RouteListEditor, type RouteMachine, type RouteRow } from '../components/route-list-editor.tsx';
import { canWriteSettings } from '../lib/privilege.ts';
import { routePhrase } from '../lib/route-list.ts';
import { useSettingsAutosave } from '../lib/settings-autosave.tsx';
import { ModelEvidencePanel } from './model-evidence-panel.tsx';
import { api, type SessionUser } from '../live/api.ts';

type AgentRole = keyof UiModelDefaults['lightRoutes'];

const LIGHT_ROLES: ReadonlyArray<{ role: AgentRole; label: string; help: string }> = [
  { role: 'implementer', label: 'Implementing', help: 'Two thirds of stage spend: the list most worth lightening.' },
  { role: 'reviewer', label: 'AI Review', help: 'Every reviewer the ticket type names, on a small ticket.' },
  { role: 'refiner', label: 'Refining', help: 'Only once a footprint estimate exists, so usually a re-refine.' },
];

interface Draft {
  agent: RouteRow[];
  utility: RouteRow[];
  light: Record<AgentRole, RouteRow[]>;
}

function draftOf(defaults: UiModelDefaults): Draft {
  return {
    light: {
      refiner: (defaults.lightRoutes?.refiner ?? []).map((route) => ({ ...route })),
      implementer: (defaults.lightRoutes?.implementer ?? []).map((route) => ({ ...route })),
      reviewer: (defaults.lightRoutes?.reviewer ?? []).map((route) => ({ ...route })),
    },
    agent: defaults.agentRoutes.map((route) => ({ ...route })),
    utility: defaults.utilityRoutes.map((route) => ({
      machine: route.machine,
      harness: route.harness,
      model: route.model,
      thinking: route.effort,
    })),
  };
}

export function ModelsSettingsPanel({ user }: { user: SessionUser | null }) {
  const canWrite = canWriteSettings(user);
  const [defaults, setDefaults] = useState<UiModelDefaults | null>(null);
  const [machines, setMachines] = useState<RouteMachine[]>([]);
  const [models, setModels] = useState<UiCatalogueModel[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const next = await api.modelDefaults();
      setDefaults(next);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([api.machines().catch(() => null), api.modelsCatalogue(null).catch(() => null)]).then(
      ([hosts, catalogue]) => {
        if (cancelled) return;
        setMachines(
          (hosts?.items ?? []).map((item) => ({ name: item.name, installedHarnesses: item.installedHarnesses, live: item.live, paused: item.paused })),
        );
        setModels(catalogue?.models ?? []);
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const persist = useCallback(async (nextDraft: Draft) => {
    const agentRoute = (row: RouteRow): UiModelDefaults['agentRoutes'][number] => ({
      machine: row.machine,
      harness: row.harness as UiModelDefaults['agentRoutes'][number]['harness'],
      model: row.model,
      thinking: row.thinking,
    });
    const next = await api.saveModelDefaults({
      agentRoutes: nextDraft.agent.map(agentRoute),
      lightRoutes: {
        refiner: nextDraft.light.refiner.map(agentRoute),
        implementer: nextDraft.light.implementer.map(agentRoute),
        reviewer: nextDraft.light.reviewer.map(agentRoute),
      },
      utilityRoutes: nextDraft.utility.map((row) => ({
        machine: row.machine,
        harness: row.harness,
        model: row.model,
        effort: row.thinking,
      })),
    });
    setDefaults(next);
  }, []);
  const autosave = useSettingsAutosave({
    value: defaults ? draftOf(defaults) : { agent: [], utility: [], light: { refiner: [], implementer: [], reviewer: [] } },
    save: persist,
    what: 'the Nightshift model defaults',
    enabled: canWrite && defaults !== null,
    delay: 700,
    validate: (next) => [...next.agent, ...next.utility, ...Object.values(next.light).flat()].some((row) => !row.harness || !row.model)
      ? 'Complete every route before it can be saved.'
      : null,
  });
  const { draft, setDraft, status, error: saveError } = autosave;
  const busy = status === 'saving';

  if (error && !defaults) {
    return (
      <Empty
        title="Model defaults could not be read."
        body={error}
        action={
          <button type="button" className="btn" onClick={() => void load()}>
            Try again
          </button>
        }
      />
    );
  }
  if (!defaults) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--s8)' }}>
        <Spinner size={20} />
      </div>
    );
  }

  const fallbackAgent = routePhrase(defaults.fallback.agent);
  const fallbackUtility = routePhrase({
    machine: defaults.fallback.utility.machine,
    harness: defaults.fallback.utility.harness,
    model: defaults.fallback.utility.model,
    thinking: defaults.fallback.utility.effort,
  });

  return (
    <div className="cfg-stack">
      <p className="sheet-note cfg-provenance">
        Rows are tried in order. A row with a machine replaces the machine-less rows on that
        machine; give every row the same machine to pin the work there. Projects narrow these
        under Project → Execution and Project → Sidecars; tickets narrow them again in the
        drawer. Reviewers have no model list of their own: they run on the Reviewer routes,
        then these.
      </p>
      <section className="sheet-panel">
        <header className="panel-head">
          <Kicker>Default agent routes</Kicker>
          <span className="cfg-help" style={{ marginLeft: 'auto' }}>
            Refining, Implementing and AI Review when nothing narrower applies.
          </span>
        </header>
        <div className="sheet-panel-body">
          <RouteListEditor
            rows={draft.agent}
            onChange={(rows) => setDraft({ ...draft, agent: rows })}
            machines={machines}
            models={models}
            disabled={!canWrite || busy}
            label="Default agent route"
            anyMachineLabel="All machines"
            emptyText={
              <>
                Nothing saved. The coded default applies: <strong>{fallbackAgent}</strong>.
              </>
            }
          />
        </div>
      </section>
      <section className="sheet-panel" aria-labelledby="light-routes-heading">
        <header className="panel-head">
          <Kicker>
            <span id="light-routes-heading">Light routes</span>
          </Kicker>
          <span className="cfg-help" style={{ marginLeft: 'auto' }}>
            Tried first on a small ticket; the default routes above take over after any failure, bounce or stall.
          </span>
        </header>
        <div className="sheet-panel-body">
          <p className="sheet-note">
            A ticket is small when its footprint estimate names at most three files (one heavy) in at most two
            directories, it has at most four acceptance criteria, and it is not graphics, performance or platform
            work; the ticket drawer can override that. Once a stage escalates it stays on the default routes
            until the stage completes. A light list applies only where these default routes do: a project that
            sets its own routes under Project → Execution sets its own light routes there. Empty lists change
            nothing.
          </p>
          {LIGHT_ROLES.map(({ role, label, help }) => (
            <div className="route-block" key={role}>
              <div className="route-block-head">
                <Kicker>{label}</Kicker>
                <span className="cfg-help">{help}</span>
              </div>
              <RouteListEditor
                rows={draft.light[role]}
                onChange={(rows) => setDraft({ ...draft, light: { ...draft.light, [role]: rows } })}
                machines={machines}
                models={models}
                disabled={!canWrite || busy}
                label={`${label} light route`}
                anyMachineLabel="All machines"
                emptyText="No light route. Every ticket runs the default agent routes in this stage."
              />
            </div>
          ))}
        </div>
      </section>
      <ModelEvidencePanel />
      <section className="sheet-panel">
        <header className="panel-head">
          <Kicker>Utility routes</Kicker>
          <span className="cfg-help" style={{ marginLeft: 'auto' }}>
            Footprints, comparisons and card titles, unless a project sets its own.
          </span>
        </header>
        <div className="sheet-panel-body">
          <RouteListEditor
            rows={draft.utility}
            onChange={(rows) => setDraft({ ...draft, utility: rows })}
            machines={machines}
            models={models}
            disabled={!canWrite || busy}
            label="Utility route"
            anyMachineLabel="Any available machine"
            emptyText={
              <>
                Nothing saved. The coded default applies: <strong>{fallbackUtility}</strong>.
              </>
            }
          />
        </div>
      </section>
      <div className="route-list-foot">
        {!canWrite ? <span className="cfg-help">Only an admin can change model defaults.</span> : null}
        {status === 'pending' ? <span className="cfg-help">Waiting to save…</span> : null}
        {busy ? <span className="cfg-help">Saving…</span> : null}
        {saveError ? <span className="cfg-error" role="alert">Unsaved: {saveError}</span> : null}
      </div>
    </div>
  );
}
